import type { CrashReport } from '@central/api';
import { color, font, formatDateTime } from '@central/ui';
import { Button, Modal } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';
import { formatBytes } from '../lib/format';
import { muted } from '../lib/labels';
import { useServices } from '../lib/environment';
import { MetricsCharts, Nodes, Services } from './EnvironmentHealth';

/**
 * The logs a crash's diagnostics cover: every service's lines in the 15
 * minutes before it. A crash names its service as GoATMS reports it (for
 * example `SIGNAL`), which is not the Loki `service` label, so the link
 * cannot filter on it.
 */
const CRASH_LOG_WINDOW_MS = 15 * 60_000;

const row: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '10px 12px',
  border: `1px solid ${color.border}`,
  borderRadius: 8,
  fontSize: 13,
};

/** "10:03" in UTC, the zone log lines are shown in. */
const hhmm = (iso: string) => new Date(iso).toISOString().slice(11, 16);

/**
 * A crash report's diagnostics (docs/design/AdminInbox.dc.html): its
 * logs, health snapshot, and CPU and memory, each read from Central at the
 * crash's time, and the uploaded bundle when there is one.
 */
export function CrashDiagnostics({ r, customerId }: { r: CrashReport; customerId: string }) {
  const [open, setOpen] = useState<'health' | 'metrics'>();
  const start = new Date(Date.parse(r.at) - CRASH_LOG_WINDOW_MS).toISOString();
  return (
    <section
      aria-labelledby={`diag-${r.id}`}
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <h3 id={`diag-${r.id}`} style={{ margin: 0, fontSize: 14 }}>
        Diagnostics bundle
      </h3>
      <div style={row}>
        <span>
          <b>Logs</b> · all services · {hhmm(start)}–{hhmm(r.at)}
        </span>
        <Link to="/logs" search={{ customerId, environmentId: r.environmentId, start, end: r.at }}>
          Open in Logs
        </Link>
      </div>
      <div style={row}>
        <span>
          <b>Health snapshot</b> · services, versions, nodes at crash time
        </span>
        <Button variant="subtle" size="compact-sm" onClick={() => setOpen('health')}>
          View
        </Button>
      </div>
      <div style={row}>
        <span>
          <b>CPU &amp; memory</b> · last 24 h
        </span>
        <Button variant="subtle" size="compact-sm" onClick={() => setOpen('metrics')}>
          View
        </Button>
      </div>
      {r.bundle ? (
        <div style={row}>
          <span>
            <b>Full bundle</b> · tar.gz as GoATMS uploaded it
          </span>
          <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <span style={{ fontFamily: font.mono, ...muted }}>{formatBytes(r.bundle.bytes)}</span>
            {r.bundle.url && (
              <a href={r.bundle.url} rel="noreferrer">
                Download bundle
              </a>
            )}
          </span>
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 13, ...muted }}>No diagnostics bundle was uploaded.</p>
      )}
      <Modal
        opened={open === 'health'}
        onClose={() => setOpen(undefined)}
        title={`Health snapshot · ${formatDateTime(r.at)}`}
        size="xl"
        closeButtonProps={{ 'aria-label': 'Close' }}
      >
        {open === 'health' && <Snapshot environmentId={r.environmentId} at={r.at} />}
      </Modal>
      <Modal
        opened={open === 'metrics'}
        onClose={() => setOpen(undefined)}
        title={`CPU & memory · 24 h before ${formatDateTime(r.at)}`}
        size="xl"
        closeButtonProps={{ 'aria-label': 'Close' }}
      >
        {open === 'metrics' && <MetricsCharts environmentId={r.environmentId} end={r.at} />}
      </Modal>
    </section>
  );
}

/** The environment's report at or before the crash: version, services and nodes. */
function Snapshot({ environmentId, at }: { environmentId: string; at: string }) {
  const version = useServices(environmentId, at).data?.version;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {version && (
        <span style={{ fontSize: 13, ...muted }}>
          Version <span style={{ fontFamily: font.mono }}>{version}</span>
        </span>
      )}
      <Services environmentId={environmentId} at={at} />
      <Nodes environmentId={environmentId} at={at} />
    </div>
  );
}
